import { useState, useEffect, useCallback } from 'react';
import { 
  Receipt, Plus, Search, RefreshCw, Eye, Pencil, Trash2, Calendar, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDateOnly } from '@/utils/dateFormat';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';

const expenseCategories = [
  { value: 'utilities', label: 'Utilities (Electricity, Water, etc.)' },
  { value: 'rent', label: 'Rent' },
  { value: 'maintenance', label: 'Maintenance & Repairs' },
  { value: 'transport', label: 'Transportation & Logistics' },
  { value: 'salary', label: 'Staff Salary' },
  { value: 'supplies', label: 'Office Supplies' },
  { value: 'equipment', label: 'Equipment Purchase' },
  { value: 'security', label: 'Security' },
  { value: 'cleaning', label: 'Cleaning Services' },
  { value: 'misc', label: 'Miscellaneous' }
];

const WarehouseExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: ''
  });
  
  const { user } = useAuth();
  const { toast } = useToast();
  const warehouseId = user?.entity_id;

  const fetchExpenses = useCallback(async () => {
    if (!warehouseId) return;
    
    setLoading(true);
    try {
      const response = await api.get('/warehouse-expenses', {
        params: { warehouse_id: warehouseId }
      });
      setExpenses(response.data || []);
    } catch (error) {
      // If API doesn't exist, show empty list
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const resetForm = () => {
    setFormData({
      category: '',
      description: '',
      amount: '',
      expense_date: new Date().toISOString().split('T')[0],
      reference_number: '',
      notes: ''
    });
    setSelectedExpense(null);
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setSelectedExpense(expense);
      setFormData({
        category: expense.category || '',
        description: expense.description || '',
        amount: expense.amount?.toString() || '',
        expense_date: expense.expense_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        reference_number: expense.reference_number || '',
        notes: expense.notes || ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.category) {
      toast({ title: 'Error', description: 'Select a category', variant: 'destructive' });
      return;
    }
    if (!formData.description) {
      toast({ title: 'Error', description: 'Enter description', variant: 'destructive' });
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Error', description: 'Enter valid amount', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        warehouse_id: warehouseId,
        warehouse_name: user?.entity_name
      };
      
      if (selectedExpense) {
        await api.put(`/warehouse-expenses/${selectedExpense.id}`, payload);
        toast({ title: 'Success', description: 'Expense updated' });
      } else {
        await api.post('/warehouse-expenses', payload);
        toast({ title: 'Success', description: 'Expense recorded' });
      }
      
      setDialogOpen(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save expense', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expense) => {
    if (!confirm('Delete this expense entry?')) return;
    
    try {
      await api.delete(`/warehouse-expenses/${expense.id}`);
      toast({ title: 'Success', description: 'Expense deleted' });
      fetchExpenses();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete expense', variant: 'destructive' });
    }
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(exp => {
    const matchesSearch = !search || 
      exp.description?.toLowerCase().includes(search.toLowerCase()) ||
      exp.reference_number?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || exp.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Summary calculations
  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
  const thisMonthExpenses = expenses.filter(exp => {
    const expDate = new Date(exp.expense_date);
    const now = new Date();
    return expDate.getMonth() === now.getMonth() && expDate.getFullYear() === now.getFullYear();
  }).reduce((sum, exp) => sum + (exp.amount || 0), 0);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="warehouse-expenses-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Warehouse Expenses</h1>
          <p className="text-gray-500">{user?.entity_name} - Track operational expenses</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchExpenses} className="border-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button 
            onClick={() => handleOpenDialog()} 
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">This Month</p>
                <p className="text-2xl font-bold text-teal-600">₹{thisMonthExpenses.toLocaleString()}</p>
              </div>
              <Calendar className="w-8 h-8 text-teal-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total (Filtered)</p>
                <p className="text-2xl font-bold text-gray-800">₹{totalExpenses.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Entries</p>
                <p className="text-2xl font-bold text-gray-800">{filteredExpenses.length}</p>
              </div>
              <Receipt className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search expenses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-300"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 bg-white border-gray-300">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">All Categories</SelectItem>
            {expenseCategories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Expenses Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No expenses recorded</p>
              <Button 
                onClick={() => handleOpenDialog()} 
                variant="outline" 
                className="mt-4 border-gray-300"
              >
                Record your first expense
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 border-gray-200">
                    <TableHead className="text-gray-600">Date</TableHead>
                    <TableHead className="text-gray-600">Category</TableHead>
                    <TableHead className="text-gray-600">Description</TableHead>
                    <TableHead className="text-gray-600">Reference</TableHead>
                    <TableHead className="text-gray-600 text-right">Amount</TableHead>
                    <TableHead className="text-gray-600 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => (
                    <TableRow key={expense.id} className="border-gray-200">
                      <TableCell className="text-gray-600">
                        {formatDateOnly(expense.expense_date)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {expense.category?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-800 max-w-xs truncate">
                        {expense.description}
                      </TableCell>
                      <TableCell className="text-gray-500">{expense.reference_number || '-'}</TableCell>
                      <TableCell className="text-right font-bold text-gray-800">
                        ₹{expense.amount?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-blue-500 hover:bg-blue-50"
                            onClick={() => handleOpenDialog(expense)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:bg-red-50"
                            onClick={() => handleDelete(expense)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800">
              {selectedExpense ? 'Edit Expense' : 'Record New Expense'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {expenseCategories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="e.g., Electricity bill for January"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Amount (₹) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Date</Label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData(p => ({ ...p, expense_date: e.target.value }))}
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Reference Number</Label>
              <Input
                value={formData.reference_number}
                onChange={(e) => setFormData(p => ({ ...p, reference_number: e.target.value }))}
                placeholder="e.g., INV-2024-001"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="bg-gray-50 border-gray-300"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              {saving ? 'Saving...' : (selectedExpense ? 'Update' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseExpensesPage;
